<!--
  Ficha do ativo em cards lado a lado. Valor de texto livre (subclasse,
  instituição, portfólio) trunca numa linha e mostra o conteúdo inteiro no
  tooltip, com atraso pra não piscar ao varrer a lista com o mouse.
-->
<script>
import TagField from '@components/tags-field'
import {
  MAX_CHARS_VALOR_SEM_TOOLTIP,
  resolveFichaGroups,
} from '@modules/consolidator/constants/AssetDetailsFicha'

export default {
  name: 'AssetFicha',
  components: { TagField },
  props: {
    asset: {
      type: Object,
      required: true,
    },
    ctx: {
      type: Object,
      required: true,
    },
  },
  computed: {
    groups() {
      return resolveFichaGroups(this.asset, this.ctx).map((group) => ({
        ...group,
        fields: (group.fields || []).map((field) => {
          const texto =
            field.value === null || field.value === undefined
              ? ''
              : String(field.value)
          return {
            ...field,
            precisaDica: texto.length > MAX_CHARS_VALOR_SEM_TOOLTIP,
          }
        }),
      }))
    },
    isConsultantContext() {
      return !this.ctx.isClient
    },
    hasTags() {
      return Boolean(
        this.isConsultantContext && this.asset.tags && this.asset.tags.length
      )
    },
    hasComentario() {
      return Boolean(this.isConsultantContext && this.asset.comentario)
    },
  },
}
</script>

<template>
  <div class="asset-ficha">
    <v-row class="ma-0">
      <v-col
        v-for="group in groups"
        :key="group.key"
        cols="12"
        md="4"
        class="py-2"
      >
        <div class="asset-ficha__group">
          <div class="asset-ficha__group-title az-ds-card-title">
            {{ group.title }}
          </div>
          <div
            v-for="field in group.fields"
            :key="field.key"
            class="asset-ficha__row"
          >
            <span class="asset-ficha__label az-text-secondary az-size-14">
              {{ field.label }}
              <v-tooltip v-if="field.info" right>
                <template v-slot:activator="{ on, attrs }">
                  <icon-svg
                    name="mdi-information-outline"
                    :size="16"
                    color="secondary"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <div class="asset-ficha__tooltip-content">
                  <span>{{ field.info }}</span>
                </div>
              </v-tooltip>
            </span>
            <v-tooltip
              v-if="field.precisaDica"
              bottom
              open-delay="400"
              max-width="320"
            >
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="asset-ficha__value asset-ficha__value--truncado az-text-primary az-size-14"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ field.value }}
                </span>
              </template>
              <span>{{ field.value }}</span>
            </v-tooltip>
            <span v-else class="asset-ficha__value az-text-primary az-size-14">
              {{ field.value }}
            </span>
          </div>
        </div>
      </v-col>
      <v-col v-if="hasTags || hasComentario" cols="12" class="py-2">
        <div class="asset-ficha__group">
          <div class="asset-ficha__group-title az-ds-card-title">
            Tags e comentários
          </div>
          <div class="asset-ficha__bloco">
            <span class="asset-ficha__label az-text-secondary az-size-14">
              Tags
            </span>
            <TagField v-if="hasTags" :value="asset.tags" readonly />
            <span v-else class="asset-ficha__value az-text-primary az-size-14">
              -
            </span>
          </div>
          <div class="asset-ficha__bloco">
            <span class="asset-ficha__label az-text-secondary az-size-14">
              Comentário
            </span>
            <p class="asset-ficha__comentario az-text-primary az-size-14">
              {{ asset.comentario || '-' }}
            </p>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<style lang="sass" scoped>
.asset-ficha
  // A `v-row` aplica margin negativa de gutter; sem o `ma-0` acima ela estoura a
  // largura do drawer e nasce um scroll horizontal no modal inteiro.
  overflow-x: hidden

  &__group
    border: 1px solid color-mix(in srgb, var(--secondary) 30%, transparent)
    border-radius: 10px
    padding: 4px 16px 8px
    height: 100%

  &__group-title
    // Só espaçamento. Tamanho, peso, família e cor vêm do `az-ds-card-title` do
    // design system — repetir qualquer um deles aqui SOBRESCREVE o DS, e foi
    // exatamente assim que o título continuou em caixa-alta depois de "corrigido".
    padding: 12px 0 4px

  &__row
    display: flex
    justify-content: space-between
    align-items: baseline
    gap: 16px
    padding: 8px 0
    border-bottom: 1px solid color-mix(in srgb, var(--secondary) 15%, transparent)

    &:last-child
      border-bottom: 0

  &__label
    flex-shrink: 0
    // Sem o flex o `v-tooltip` renderiza um wrapper que quebra a linha e o icone
    // cai ABAIXO do rotulo. O `display: contents` tira esse wrapper 0x0 do
    // layout (mesmo padrao do `asset-header.vue`); o `inline-flex` garante que
    // rotulo e icone fiquem na mesma linha, centralizados.
    display: inline-flex
    align-items: center
    gap: 4px

    ::v-deep .v-tooltip
      display: contents

  &__value
    font-weight: 600
    text-align: right
    min-width: 0
    overflow-wrap: anywhere

    &--truncado
      overflow: hidden
      text-overflow: ellipsis
      white-space: nowrap
      overflow-wrap: normal

  &__bloco
    display: flex
    flex-direction: column
    gap: 6px
    padding: 8px 0
    border-bottom: 1px solid color-mix(in srgb, var(--secondary) 15%, transparent)

    &:last-child
      border-bottom: 0

  &__comentario
    margin: 0
    // Comentario e texto livre digitado pelo usuario: preserva a quebra de linha
    // que ele escreveu, sem virar um textarea desabilitado.
    white-space: pre-line
    overflow-wrap: anywhere

  &__tooltip-content
    max-width: 200px
</style>
